import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// 22 arcanos maiores — dados modelo (nome + frase-síntese)
const ARCANA = [
  { n: '0', name: 'O Louco', line: 'Comece: o passo novo pede corpo inteiro, não certeza.' },
  { n: 'I', name: 'O Mago', line: 'Você já tem as ferramentas — o que falta é usar todas.' },
  { n: 'II', name: 'A Papisa', line: 'Há uma resposta que só o silêncio devolve. Espera ouvida.' },
  { n: 'III', name: 'A Imperatriz', line: 'Cuide como quem cultiva: crescimento é rotina, não surto.' },
  { n: 'IV', name: 'O Imperador', line: 'Estrutura liberta: uma régua clara poupa metade da briga.' },
  { n: 'V', name: 'O Hierofante', line: 'Tradição tem valor — mas questione o que veio de graça.' },
  { n: 'VI', name: 'Os Enamorados', line: 'A escolha é real e as duas opções têm preço. Escolha consciente.' },
  { n: 'VII', name: 'O Carro', line: 'Momentum ganho se sustenta com direção, não com pressa.' },
  { n: 'VIII', name: 'A Força', line: 'Firmeza macia: segure sem esmagar.' },
  { n: 'IX', name: 'O Eremita', line: 'Uma semana de clareza vale um mês de ruído. Retire-se um pouco.' },
  { n: 'X', name: 'A Roda', line: 'O ciclo virou: não agarre o que está de partida.' },
  { n: 'XI', name: 'A Justiça', line: 'A conta fecha: pondere com os dois olhos abertos.' },
  { n: 'XII', name: 'O Pendurado', line: 'Pausa produtiva: ver de cabeça para baixo também é ver.' },
  { n: 'XIII', name: 'A Morte', line: 'Algo termina para que outra coisa caiba. Deixe terminar.' },
  { n: 'XIV', name: 'A Temperança', line: 'O meio do caminho não é fraqueza — é técnica.' },
  { n: 'XV', name: 'O Diabo', line: 'Olhe o padrão que você chama de hábito. Ele tem correntes?' },
  { n: 'XVI', name: 'A Torre', line: 'O que cai não era sustentáculo. Reconstruir é permitido.' },
  { n: 'XVII', name: 'A Estrela', line: 'Confiança devagar: a estrela guia, não conserta.' },
  { n: 'XVIII', name: 'A Lua', line: 'Nem tudo que brilha à noite é caminho — confirme antes de andar.' },
  { n: 'XIX', name: 'O Sol', line: 'Dia limpo: mostre o que fez — o aplauso vem.' },
  { n: 'XX', name: 'O Juízo', line: 'A chamada que você ensaiou: atenda pela metade que assusta menos.' },
  { n: 'XXI', name: 'O Mundo', line: 'Ciclo completo: dança antes de começar o próximo.' },
];

export const TarotPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cardIndex, setCardIndex] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);

  const drawCard = () => {
    setCardIndex(Math.floor(Math.random() * ARCANA.length));
    setRevealed(false);
    // pequeno atraso para o flip acontecer depois do embaralhar
    setTimeout(() => setRevealed(true), 120);
  };

  const card = cardIndex !== null ? ARCANA[cardIndex] : null;

  return (
    <div className="min-h-[calc(100vh-4rem)] text-stela-fg">
      <main className="max-w-[640px] mx-auto px-4 py-8 text-center">
        <Link to="/" className="text-stela-muted text-sm hover:text-stela-lilac block text-left">← Voltar</Link>
        <h1 className="font-display font-semibold text-3xl mt-3">Tarot do Dia</h1>
        <p className="text-stela-muted text-sm mt-2 max-w-[45ch] mx-auto">
          Uma carta, um recado. Puxada por Stela com a leitura do seu mapa em vista.
        </p>

        <div className="mt-8 flex flex-col items-center" style={{ perspective: '800px' }}>
          <button
            onClick={card ? undefined : drawCard}
            aria-label={card ? card.name : 'Puxar carta'}
            className="w-[170px] h-[250px] relative rounded-xl border border-stela-lilac/25 focus:outline-none focus-visible:outline-2 focus-visible:outline-stela-accent"
            style={{
              transformStyle: 'preserve-3d',
              transform: card && revealed ? 'rotateY(0deg)' : 'rotateY(180deg)',
              transition: 'transform .6s ease',
              background: card
                ? 'linear-gradient(160deg,#2E2752,#1A1440)'
                : 'linear-gradient(160deg,#2E2752,#1A1440)',
            }}
          >
            {card ? (
              <span className="absolute inset-0 flex flex-col items-center justify-center px-3 gap-3" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                <span className="font-display text-2xl text-stela-lilac">{card.name}</span>
                <span className="text-stela-gold text-xs tracking-[.2em]">{card.n}</span>
                <span className="text-4xl text-stela-accent">✦</span>
              </span>
            ) : (
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <span className="text-5xl text-stela-accent">✧</span>
                <span className="text-stela-muted text-xs tracking-[.18em] uppercase">Puxe sua carta</span>
              </span>
            )}
          </button>

          {card && revealed && (
            <div className="mt-6">
              <p className="font-display text-lg text-stela-lilac max-w-[38ch]">
                “{card.line}”
              </p>
              {!user && (
                <p className="text-stela-muted text-xs mt-2">
                  Crie sua conta — a leitura diária guarda o seu histórico de cartas.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-8">
          {card ? (
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-stela-accent to-stela-accent2 text-white font-semibold text-sm px-6 py-2.5 rounded-full hover:brightness-110 transition"
            >
              Falar com Stela sobre esta carta
            </button>
          ) : (
            <button
              onClick={drawCard}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-[#F3D488] to-[#D9A640] text-[#241A05] font-semibold text-sm px-6 py-2.5 rounded-full hover:brightness-110 transition"
            >
              Puxar minha carta de hoje
            </button>
          )}
        </div>

        {card && revealed && (
          <button onClick={drawCard} className="text-stela-muted text-xs mt-4 hover:text-stela-lilac underline">
            embaralhar de novo
          </button>
        )}
      </main>
    </div>
  );
};